import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "wouter";
import { ChevronRight, ExternalLink, MapPin, Check, Share2 } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { subscribeToNewsletter } from "@/lib/brevo";

// Hero image — masonry collage of actual eLicitațiiANAF assets
const HERO_IMG = "https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/tqdGOmhsJOvWFMNk.webp";

const ARTICLE1_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/hero-article1-v1-mBURY4muT8nNAjUnDwQRbz.webp";
const ARTICLE3_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/article3-residential-complex-Vstb4C43kK3pRH4WYGacpF.webp";
const ARTICLE8_IMG = "https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/cbxBOxJCiaYOARrK.webp";

const RELATED = [
  { tag: "Real Estate", title: "Buying Property in Brașov in 2026: A Complete Guide for Foreigners", img: ARTICLE3_IMG, href: "/buying-apartment-brasov" },
  { tag: "Business Setup", title: "Company Setup Romania 2026: Complete Guide for Expats", img: ARTICLE8_IMG, href: "/company-setup-romania" },
  { tag: "City Guide", title: "Why Move to Brașov? A Foreigner's Honest Guide to Romania's Best City", img: ARTICLE1_IMG, href: "/why-expats-choose-brasov" },
];

function NewsletterInlineLight() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setStatus("loading");
    try {
      await subscribeToNewsletter(email);
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  if (status === "success") return <p className="text-xs text-green-700 py-2">✓ Subscribed! See you Friday.</p>;

  return (
    <form onSubmit={handleSubmit}>
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="your@email.com"
        className="w-full border border-stone-200 px-3 py-2 text-xs text-stone-700 outline-none focus:border-stone-400 mb-2"
      />
      <button
        type="submit"
        disabled={status === "loading"}
        className="w-full py-2 text-xs text-white font-semibold disabled:opacity-60"
        style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}
      >
        {status === "loading" ? "..." : "SUBSCRIBE"}
      </button>
      {status === "error" && <p className="text-xs text-red-500 mt-1">Try again.</p>}
    </form>
  );
}

function SidebarAd() {
  return (
    <div className="border border-stone-200 overflow-hidden">
      <div className="px-3 py-1.5" style={{ backgroundColor: "oklch(0.95 0.008 75)" }}>
        <span className="text-xs text-stone-400 uppercase tracking-widest">Partner Content · Cartier Transilvania</span>
      </div>
      <div className="relative">
        <img
          src="https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/XbbLvxSueOHmjHcF.webp"
          alt="Cartier Transilvania Brașov"
          className="w-full h-48 object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, oklch(0.12 0.01 60 / 0.8) 0%, transparent 60%)" }} />
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <p className="text-white text-xs uppercase tracking-widest mb-1 font-semibold">New development · Noua</p>
          <h4 className="text-white font-bold leading-tight" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.1rem" }}>
            Cartier Transilvania
          </h4>
        </div>
      </div>
      <div className="p-4">
        <p className="text-stone-600 text-sm font-light leading-relaxed mb-3">
          208 apartments in 6 blocks. T1–T3 completed &amp; occupied. T4–T6 delivering September 2027. Next to Lake Noua.
        </p>
        <div className="flex items-center gap-1 text-xs text-stone-500 mb-4">
          <MapPin size={11} />
          <span>Noua, Brașov</span>
        </div>
        <a
          href="https://www.cartiertransilvania.ro/en/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-1 text-xs text-white w-full px-3 py-2 transition-opacity hover:opacity-90"
          style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}
        >
          VIEW PROJECT <ExternalLink size={10} />
        </a>
      </div>
    </div>
  );
}

function FeaturedDeveloper() {
  return (
    <div className="mt-16 p-8 border-l-4" style={{ backgroundColor: "oklch(0.985 0.006 75)", borderLeftColor: "oklch(0.38 0.12 15)" }}>
      <div className="flex items-center gap-2 mb-5">
        <span className="text-xs text-stone-400 uppercase tracking-widest">Partner content · Cartier Transilvania</span>
      </div>
      <div className="flex flex-col md:flex-row gap-6 items-start">
        <img
          src="https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/XbbLvxSueOHmjHcF.webp"
          alt="Cartier Transilvania"
          className="w-full md:w-56 h-40 object-cover flex-shrink-0"
          loading="lazy"
        />
        <div>
          <p className="text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: "oklch(0.38 0.12 15)" }}>
            Apartments for sale · Noua, Brașov
          </p>
          <h3 className="font-bold text-stone-900 mb-3 leading-snug" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.3rem" }}>
            Cartier Transilvania — a development built to last
          </h3>
          <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.7" }}>
            208 apartments across 6 blocks in the Noua neighbourhood. T1, T2, and T3 are completed and occupied. T4, T5, and T6 are under construction with a delivery date of September 2027. Solar panels and heat pump heating system included. Next to Lake Noua, Brașov Zoo, and Parc Aventura.
          </p>
          <a
            href="https://www.cartiertransilvania.ro/en/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-xs text-white px-5 py-2.5 transition-opacity hover:opacity-90"
            style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}
          >
            VISIT PROJECT SITE <ExternalLink size={11} />
          </a>
        </div>
      </div>
    </div>
  );
}

function ShareButton({ url, title }: { url: string; title: string }) {
  const [copied, setCopied] = useState(false);
  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, url });
      } catch { /* cancelled */ }
    } else {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  return (
    <button onClick={handleShare} className="flex items-center gap-2 text-xs text-stone-500 hover:text-stone-800 transition-colors">
      {copied ? <Check size={13} className="text-green-600" /> : <Share2 size={13} />}
      {copied ? "Copied!" : "Share"}
    </button>
  );
}

export default function ArticleBuyPropertyAuctions() {
  return (
    <>
      <Helmet>
        <title>How to buy property below market value in Romania (2026)</title>
        <meta name="description" content="A complete guide for foreign investors on buying property and assets below market value in Romania through ANAF government auctions and bank foreclosures." />
        <meta property="og:title" content="How to buy property below market value in Romania (2026)" />
        <meta property="og:description" content="A complete guide for foreign investors on buying property and assets below market value in Romania through ANAF government auctions and bank foreclosures." />
        <meta property="og:image" content={HERO_IMG} />
        <meta property="og:image:width" content="1600" />
        <meta property="og:image:height" content="900" />
        <meta property="og:image:alt" content="Buying property below market value in Romania through government auctions" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://brasovgateway.com/buy-property-romania-auctions-seized-assets" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="How to buy property below market value in Romania (2026)" />
        <meta name="twitter:description" content="A complete guide for foreign investors on buying property and assets below market value in Romania through ANAF government auctions and bank foreclosures." />
        <meta name="twitter:image" content={HERO_IMG} />
        <meta name="keywords" content="buy property Romania, Romania real estate investment, Romania government auction, ANAF auction Romania, buy seized assets Romania, elicitatii anaf" />
        <link rel="canonical" href="https://brasovgateway.com/buy-property-romania-auctions-seized-assets" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Article",
                "@id": "https://brasovgateway.com/buy-property-romania-auctions-seized-assets#article",
                "headline": "How to buy property below market value in Romania (2026)",
                "description": "A complete guide for foreign investors on buying property and assets below market value in Romania through ANAF government auctions and bank foreclosures.",
                "image": "${HERO_IMG}",
                "datePublished": "2026-04-21",
                "dateModified": "2026-04-21",
                "author": {
                  "@type": "Organization",
                  "name": "Brașov Gateway",
                  "url": "https://brasovgateway.com"
                },
                "publisher": {
                  "@type": "Organization",
                  "name": "Brașov Gateway",
                  "url": "https://brasovgateway.com"
                },
                "url": "https://brasovgateway.com/buy-property-romania-auctions-seized-assets",
                "inLanguage": "en",
                "articleSection": "Real Estate"
              },
              {
                "@type": "BreadcrumbList",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://brasovgateway.com" },
                  { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://brasovgateway.com/blog" },
                  { "@type": "ListItem", "position": 3, "name": "Buying property below market value in Romania", "item": "https://brasovgateway.com/buy-property-romania-auctions-seized-assets" }
                ]
              }
            ]
          }
        `}</script>
      </Helmet>

      <div className="min-h-screen" style={{ fontFamily: "Source Sans 3, sans-serif", backgroundColor: "oklch(0.985 0.006 75)" }}>
        <SiteHeader />

        {/* Hero */}
        <div className="relative h-72 md:h-96 mt-16">
          <img
            src={HERO_IMG}
            alt="Buying property below market value in Romania through government auctions"
            className="w-full h-full object-cover"
            fetchPriority="high"
            width="1400"
            height="788"
          />
          <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, transparent 40%, oklch(0.985 0.006 75) 100%)" }} />
        </div>

        <div className="container py-8">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Main content */}
            <main className="flex-1 min-w-0">
              <article className="max-w-none">
                <header className="mb-10">
                  <div className="flex items-center gap-3 mb-6">
                    <span className="text-xs font-bold uppercase tracking-widest px-3 py-1" style={{ backgroundColor: "oklch(0.38 0.12 15)", color: "white" }}>
                      Real Estate
                    </span>
                    <span className="text-xs text-stone-500 uppercase tracking-widest">April 2026</span>
                  </div>
                  <h1 className="text-stone-900 leading-tight mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 800 }}>
                    How to buy property below market value in Romania: the 2026 guide to auctions and seized assets
                  </h1>
                  <div className="flex items-center justify-between py-4 border-t border-b border-stone-200">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full overflow-hidden" style={{ backgroundColor: "oklch(0.95 0.008 75)" }}>
                        <img
                          src="https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/XbbLvxSueOHmjHcF.webp"
                          alt="Brașov Gateway"
                          className="w-full h-full object-cover opacity-50"
                        />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-stone-900">Brașov Gateway Editorial</p>
                        <p className="text-xs text-stone-500">Investment &amp; Real Estate Team</p>
                      </div>
                    </div>
                    <ShareButton url="https://brasovgateway.com/buy-property-romania-auctions-seized-assets" title="How to buy property below market value in Romania" />
                  </div>
                </header>

                {/* Lead paragraph */}
                <p className="text-stone-600 text-lg md:text-xl font-light leading-relaxed mt-6 mb-8" style={{ lineHeight: "1.6" }}>
                  Everyone wants to buy property "below market value," but in Romania that usually means navigating a system that was, until recently, designed to keep outsiders out. March 2026 changed that. Here is the honest guide for foreign investors who want to understand what is actually available — and what it costs to get it.
                </p>

                {/* Quick Facts */}
                <div className="my-10 p-6 border-l-4" style={{ backgroundColor: "oklch(0.97 0.005 75)", borderLeftColor: "oklch(0.38 0.12 15)" }}>
                  <h3 className="text-xs uppercase tracking-widest font-bold mb-5" style={{ color: "oklch(0.38 0.12 15)" }}>
                    Quick facts: Romania's government auction market
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[
                      { label: "Platform launched", value: "March 30, 2026", note: "eLicitațiiANAF — first public digital auction portal" },
                      { label: "Day-one traffic", value: "14 million accesses", note: "In the first 24 hours after launch" },
                      { label: "Assets at launch", value: "171 items listed", note: "Apartments, vehicles, land, equipment" },
                      { label: "Price reduction mechanism", value: "−25% / −50%", note: "At 2nd and 3rd auction rounds if unsold" },
                      { label: "Annual recovery (ANAF)", value: "~€31M / year", note: "Via enforcement proceedings" },
                      { label: "ANABI parallel system", value: ">€200M total", note: "Judicial confiscations (criminal cases) managed separately from ANAF tax enforcement" },
                    ].map((fact, i) => (
                      <div key={i} className="p-4 border border-stone-100" style={{ backgroundColor: "oklch(0.985 0.006 75)" }}>
                        <p className="text-xs uppercase tracking-widest text-stone-400 mb-1">{fact.label}</p>
                        <p className="font-bold text-stone-900 text-sm leading-tight mb-1" style={{ fontFamily: "Playfair Display, serif" }}>{fact.value}</p>
                        <p className="text-xs text-stone-500 font-light">{fact.note}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Table of Contents */}
                <div className="my-8 p-6 border border-stone-200" style={{ backgroundColor: "oklch(0.97 0.005 75)" }}>
                  <p className="text-xs uppercase tracking-widest font-semibold mb-4" style={{ color: "oklch(0.38 0.12 15)" }}>In this guide</p>
                  <ol className="space-y-2">
                    {[
                      { n: "01", label: "The new player: ANAF's digital auction platform", anchor: "#anaf-platform" },
                      { n: "02", label: "Romania in a European context: how it compares", anchor: "#european-context" },
                               { n: "03", label: "Can foreigners actually buy seized assets?", anchor: "#foreigners" },
                      { n: "04", label: "The bank foreclosure route (executări silite)", anchor: "#bank-foreclosures" },
                      { n: "05", label: "The risks nobody tells you about", anchor: "#risks" },
                      { n: "06", label: "Who buys at government auctions — and why", anchor: "#who-buys" },
                      { n: "07", label: "Is it worth the hassle in Brașov?", anchor: "#brasov" },
                    ].map((item) => (
                      <li key={item.n} className="flex items-start gap-3">
                        <span className="text-xs font-bold flex-shrink-0 mt-0.5" style={{ color: "oklch(0.38 0.12 15)" }}>{item.n}</span>
                        <a href={item.anchor} className="text-stone-600 text-sm font-light hover:text-stone-900 transition-colors no-underline" style={{ lineHeight: "1.5" }}>
                          {item.label}
                        </a>
                      </li>
                    ))}
                  </ol>
                </div>

                {/* Section 1 */}
                <h2 id="anaf-platform" className="text-stone-900 mt-12 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                  The new player: ANAF's digital auction platform
                </h2>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  On March 30, 2026, the Romanian tax authority launched{" "}
                  <a
                    href="https://elicitatii.anaf.ro/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline decoration-dotted underline-offset-2 hover:text-stone-900 transition-colors"
                    style={{ color: "oklch(0.38 0.12 15)" }}
                  >
                    eLicitațiiANAF
                  </a>
                  , a centralised digital portal for auctioning seized and state-held assets. The platform, built by Zitec and funded through Romania's PNRR reform programme, integrates for the first time both criminally confiscated goods and assets seized through tax enforcement — in a single publicly accessible interface. Before this, these auctions existed, but in a closed circuit: announced in the Official Gazette and local newspapers, accessible mainly to those with legal connections or insider knowledge.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  The market's response was immediate. In the first 24 hours, the platform registered over 14 million accesses — confirming a latent demand that had been building for years. At launch, 171 items were listed, ranging from apartments and commercial spaces to vehicles, land plots, and industrial equipment. The pricing mechanism is what makes it genuinely interesting to investors: if an asset does not sell at the first auction, the starting price for the second drops by 25%. If it still does not sell, the third auction begins at 50% of the initial valuation. Each auction cycle is preceded by a 30-day public advertising period, during which prospective buyers can request to view the asset in person.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  The platform is currently built primarily for Romanian citizens and companies. The interface is entirely in Romanian, and the registration process requires integration with the SPV (Spațiul Privat Virtual) — ANAF's internal digital identity system. This is not a minor detail for foreign investors, and we address it directly in the section below.
                </p>

                {/* Section 2 */}
                <h2 id="european-context" className="text-stone-900 mt-12 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                  Romania in a European context: how it compares
                </h2>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  Romania is not the first country to build a government auction platform, but it is one of the later adopters in the EU — and it has launched with a more modern architecture than most of its predecessors. The European market for judicial and fiscal auctions remains highly fragmented: according to the EU-funded LEILA project, only 11 member states actively promote seized assets on government-managed websites, and almost all of them operate exclusively in their local language.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  The table below compares the main national platforms across Europe and the US, to give a sense of where Romania now sits in this landscape.
                </p>

                {/* Comparison Table */}
                <div className="my-6 w-full overflow-x-auto" style={{ WebkitOverflowScrolling: "touch" as React.CSSProperties["WebkitOverflowScrolling"] }}>
                  <table className="border-collapse" style={{ width: "100%", fontSize: "clamp(0.65rem, 2.2vw, 0.875rem)" }}>
                    <thead>
                      <tr style={{ backgroundColor: "oklch(0.95 0.008 75)" }}>
                        {["Country / Platform", "Asset types", "Scale / traffic", "Launched"].map((h) => (
                          <th key={h} className="text-left text-stone-600 font-semibold uppercase tracking-wider border-b border-stone-200" style={{ padding: "0.6rem 0.8rem", fontSize: "0.7rem", letterSpacing: "0.05em" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { country: "🇩🇪 Germany — Zoll-Auktion", assets: "Customs & authority seizures", scale: "1.1M visits/month (Mar 2026)", launched: "2000s" },
                        { country: "🇩🇪 Germany — VEBEG", assets: "Federal surplus, military", scale: "20,000 lots/year", launched: "1951" },
                        { country: "🇪🇸 Spain — BOE Subastas", assets: "Judicial, fiscal, administrative", scale: "Mature ecosystem + AI tools", launched: "2015" },
                        { country: "🇮🇹 Italy — PVP", assets: "Judicial sales", scale: "National coverage", launched: "2018" },
                        { country: "🇫🇷 France — Domaines DGFiP", assets: "State property, public sales", scale: "National coverage", launched: "2010s" },
                        { country: "🇭🇺 Hungary — EAS (NAV)", assets: "Seized mobile & real estate", scale: "National coverage", launched: "2010s" },
                        { country: "🇺🇸 USA — GovDeals", assets: "All government surplus", scale: "6M buyers, $836M GMV/year", launched: "2001" },
                        { country: "🇷🇴 Romania — eLicitațiiANAF", assets: "Confiscated + fiscally seized", scale: "14M accesses on day one", launched: "March 2026" },
                      ].map((row, i) => (
                        <tr key={i} className="border-b border-stone-100" style={{ backgroundColor: i % 2 === 0 ? "oklch(0.985 0.006 75)" : "white" }}>
                          <td className="text-stone-800 font-medium" style={{ padding: "0.55rem 0.8rem", lineHeight: 1.4 }}>{row.country}</td>
                          <td className="text-stone-500 font-light" style={{ padding: "0.55rem 0.8rem", lineHeight: 1.4 }}>{row.assets}</td>
                          <td className="text-stone-700" style={{ padding: "0.55rem 0.8rem", lineHeight: 1.4 }}>{row.scale}</td>
                          <td className="text-stone-500 font-light" style={{ padding: "0.55rem 0.8rem", lineHeight: 1.4 }}>{row.launched}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  The German Zoll-Auktion is the most instructive benchmark. With 1.1 million monthly visits, an average session time of over 10 minutes, and a bounce rate of just 33%, it demonstrates the level of engagement that a well-run government auction platform can sustain. One data point worth noting: Romania is consistently among the top three source countries for Zoll-Auktion's international traffic. Romanians were already hunting for seized assets abroad — they were simply waiting for a domestic platform to do the same.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  At the other end of the scale, the US market shows what maturity looks like. GovDeals, operated by Liquidity Services (NASDAQ: LQDT), connects over 15,000 government agencies with 6 million registered buyers and processes more than $836 million in gross merchandise volume per year. The global online auction market as a whole is valued at $5.6 billion in 2025 and is projected to reach $11.8 billion by 2034. Romania is entering this space at exactly the right moment — before the ecosystem of professional buyers, aggregators, and advisory services has had time to form.
                </p>

                {/* Section 3 */}
                <h2 id="foreigners" className="text-stone-900 mt-12 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                  Can foreigners actually buy seized assets?
                </h2>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  Yes — but the bureaucracy is genuinely tricky, and it is worth being honest about that upfront. The same legal framework that governs standard property purchases applies here. EU citizens have identical rights to Romanian nationals when buying apartments or commercial spaces. Non-EU citizens can buy apartments directly, but cannot own land in their own name — for anything involving land (houses, commercial plots, agricultural land), you will need to{" "}
                  <Link href="/company-setup-romania" className="underline decoration-dotted underline-offset-2 hover:text-stone-900 transition-colors" style={{ color: "oklch(0.38 0.12 15)" }}>
                    set up a Romanian SRL
                  </Link>{" "}
                  first.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  The friction point is account registration. To bid on eLicitațiiANAF, you need an active SPV (Spațiul Privat Virtual) account — ANAF's internal digital identity system. Before you can register in SPV, you first need a <strong>NIF (Număr de Identificare Fiscală)</strong> — Romania's tax identification number for non-residents. This is obtained by submitting Form 030 in person or through a representative at any ANAF office, with your passport. The process is usually completed the same day.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  Once you have a NIF, there are two practical routes to activate your SPV account. The first is through the <strong>RO e-Factura non-resident portal</strong>: ANAF provides a dedicated registration path at <em>ANAF &gt; Online services &gt; Registration of users &gt; NON-RESIDENTS</em>, which works entirely online via email verification — no physical presence required. The second route is standard SPV registration with <strong>video identification</strong>: you register on the portal, upload a scan of your passport, and schedule a short Zoom call with an ANAF operator who verifies your identity in real time. Both options are remote. A third option — scheduling a phone call with an ANAF operator — is also available during the registration process if you prefer to be guided through the steps verbally.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  In practice, the process is manageable but not intuitive — the interface is entirely in Romanian, the steps are not well documented in English, and small errors can cause delays. If you want to get this right the first time,{" "}
                  <a href="/#contact" className="underline decoration-dotted underline-offset-2 hover:text-stone-900 transition-colors" style={{ color: "oklch(0.38 0.12 15)" }}>
                    reach out via the contact page
                  </a>{" "}
                  — we have helped foreign investors navigate the NIF and SPV registration process and can walk you through it step by step.
                </p>

                {/* Section 4 */}
                <h2 id="bank-foreclosures" className="text-stone-900 mt-12 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                  The bank foreclosure route (executări silite)
                </h2>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  Alongside ANAF auctions, a parallel market exists in bank foreclosures — known in Romanian as executări silite. These occur when a property owner defaults on a mortgage and the bank initiates enforcement proceedings through a licensed bailiff (executor judecătoresc). This is a distinct channel with its own mechanics, and worth understanding as part of the same investment strategy.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  One notable difference is title history. Because a bank previously issued a mortgage on the property, the title was checked at the time of the original loan — which gives you a clearer starting point for due diligence. The bank's goal is to recover the outstanding debt, so starting prices tend to reflect a realistic valuation rather than an inflated one. Listings can be found on aggregator sites such as executari.com or directly on the websites of major Romanian banks including Banca Transilvania, BCR, and BRD.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  One practical note: bank foreclosure auctions do not require an SPV account or NIF to participate. The bidding process is managed directly by the bailiff's office. That said, the same risks that apply to ANAF auctions — occupied properties, hidden debts, limited inspection access — apply here as well. The channel is different; the due diligence requirements are not.
                </p>

                {/* Section 5 */}
                <h2 id="risks" className="text-stone-900 mt-12 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                  The risks nobody tells you about
                </h2>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  A property at 25% below market value sounds straightforward until you understand why it is discounted. Government and bank auctions in Romania come with specific risks that are absent from standard real estate transactions, and they are worth understanding before you bid.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  The most significant risk is <strong>eviction</strong>. Winning an auction gives you legal title to the property — it does not give you an empty apartment. If the former owners or tenants refuse to vacate, you must initiate formal eviction proceedings through the courts. Under{" "}
                  <a href="https://legislatie.just.ro/Public/DetaliiDocument/175630" target="_blank" rel="noopener noreferrer" className="underline decoration-dotted underline-offset-2 hover:text-stone-900 transition-colors" style={{ color: "oklch(0.38 0.12 15)" }}>
                    Romania's Code of Civil Procedure (art. 1033–1048)
                  </a>
                  , enforcement proceedings for immovable property follow a strict procedural timeline that can stretch over several months. Evictions are also legally suspended during winter months (November to March) for vulnerable individuals — meaning a contested property could remain occupied for the better part of a year after you have paid for it.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  The second risk is <strong>hidden debts</strong>. While the auction process clears the property of the specific mortgage or tax lien that triggered the sale, there may be outstanding debts to the homeowners' association (asociația de proprietari) or to utility providers.{" "}
                  <a href="https://legislatie.just.ro/Public/DetaliiDocument/202516" target="_blank" rel="noopener noreferrer" className="underline decoration-dotted underline-offset-2 hover:text-stone-900 transition-colors" style={{ color: "oklch(0.38 0.12 15)" }}>
                    Law 196/2018 on homeowners' associations (art. 77)
                  </a>{" "}
                  establishes that unpaid maintenance contributions follow the property, not the person — which means they can become your liability. A thorough check with the local homeowners' association before bidding is not optional — it is essential.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  Finally, there is the <strong>condition of the asset</strong>. Unlike a standard purchase, you typically cannot conduct a thorough inspection before bidding. You can view the property during the advertised viewing period, but you cannot commission a structural survey or test the systems. What you see is what you get — and sometimes what you get has been stripped of fixtures, damaged by the former occupants, or left unmaintained for years. Factor in a realistic renovation budget before calculating whether the auction price is actually a discount.
                </p>

                {/* Section 6 */}
                <h2 id="who-buys" className="text-stone-900 mt-12 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                  Who buys at government auctions — and why
                </h2>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  Understanding who your competition is at auction is as important as understanding the asset itself. Based on data from comparable platforms in Germany, Spain, and the US, four buyer profiles dominate these markets.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  <strong className="text-stone-800">Professional resellers</strong> are the backbone of demand. They buy in bulk — electronics, clothing lots, equipment — recondition or repackage, and sell on secondary markets like OLX, eBay, or specialist trade platforms. This segment is growing fast among younger entrepreneurs who use government auctions to stock their e-commerce operations. On platforms like GovDeals, this profile accounts for a significant share of repeat buyers.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  <strong className="text-stone-800">Real estate investors</strong> are the most relevant segment for the readers of this site. They target apartments, houses, and commercial spaces with the intention of renovating and reselling (flipping) or renting. In Spain, the BOE Subastas platform has generated an entire ecosystem of advisory services, including AI tools that score each listed property from 0 to 100 based on estimated market value versus auction starting price. Romania does not yet have this infrastructure — which is itself an opportunity for early movers.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  <strong className="text-stone-800">SMEs</strong> use these platforms to acquire vehicles, industrial equipment, and office furniture at a fraction of retail cost. For a small business in Romania, where capital costs are a significant barrier, buying a seized van or a set of machinery at 40–50% of market value can meaningfully change the economics of the operation.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  <strong className="text-stone-800">Bargain hunters</strong> — the general public attracted by the possibility of buying a car, a piece of jewellery, or a smartphone at a low starting price — account for the vast majority of traffic but a smaller share of completed transactions. The 14 million accesses on day one were largely this group. They are real, they are numerous, and they occasionally find genuine deals. But they are also the most likely to be discouraged by the bureaucratic friction of actually completing a purchase.
                </p>

                {/* Section 7 */}
                <h2 id="brasov" className="text-stone-900 mt-12 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                  Is it worth the hassle in Brașov?
                </h2>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  Brașov is one of Romania's most competitive real estate markets. As we detailed in our guide on{" "}
                  <Link href="/buying-apartment-foreigner-brasov" className="underline decoration-dotted underline-offset-2 hover:text-stone-900 transition-colors" style={{ color: "oklch(0.38 0.12 15)" }}>
                    buying an apartment in Brașov as a foreigner
                  </Link>
                  , demand consistently outstrips supply in desirable neighbourhoods, and prices have roughly doubled over the past decade. That dynamic does not disappear at auction.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  In a high-demand market like Brașov, genuinely attractive properties rarely survive to the second or third auction round. When a clean apartment in Schei or Tractorul hits the auction block, local investors and professionals who monitor these listings daily tend to move first and move fast. The properties that linger — and reach the 50% discount stage — are usually there for a reason: difficult occupants, structural problems, legal complications, or a location that the market has already priced correctly.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  That said, the market is new. eLicitațiiANAF has been live for less than two months. The infrastructure of professional buyers who monitor it systematically does not yet exist in Romania the way it does in Germany or Spain. For an investor willing to do the work — track listings consistently, understand the legal process, and move quickly when something genuinely good appears — there is a window of opportunity that will not stay open indefinitely.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  The most realistic use case for a foreign investor in Brașov is not to replace a standard property search with an auction strategy, but to run both in parallel. Keep an eye on eLicitațiiANAF and the bank foreclosure listings. When something interesting appears in a neighbourhood you know, with a title history you can verify, and at a price that still makes sense after accounting for renovation and legal costs — that is when you move. Not before.
                </p>

                {/* Blockquote */}
                <blockquote
                  className="my-8 pl-6 border-l-4 italic text-stone-600"
                  style={{ borderLeftColor: "oklch(0.38 0.12 15)", fontSize: "1.1rem", lineHeight: "1.7" }}
                >
                  "Government auctions are not for beginners looking for a holiday home. They are for investors with patience, liquid cash, a good local lawyer, and the discipline to walk away from anything that does not check every box. If you want a straightforward purchase, the standard market or a reputable new development will serve you better."
                </blockquote>

                {/* Auction Monitoring Service Callout */}
                <div className="my-10 p-6 border-2" style={{ borderColor: "oklch(0.38 0.12 15)", backgroundColor: "oklch(0.97 0.005 75)" }}>
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full text-white text-lg" style={{ backgroundColor: "oklch(0.38 0.12 15)" }}>
                      🔔
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: "oklch(0.38 0.12 15)" }}>
                        Don't speak Romanian? We'll watch the auctions for you.
                      </p>
                      <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                        If you are interested in a specific type of asset — a car, a property, industrial equipment — but the language barrier or the complexity of the platform is holding you back, we can monitor eLicitațiiANAF on your behalf. We will alert you when something matching your criteria appears, explain the listing in English, and guide you through the bidding process step by step.
                      </p>
                      <a
                        href="/#contact"
                        className="inline-flex items-center gap-2 text-xs text-white px-5 py-2.5 transition-opacity hover:opacity-90 no-underline"
                        style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}
                      >
                        SET UP AUCTION ALERTS <ChevronRight size={11} />
                      </a>
                    </div>
                  </div>
                </div>

                {/* CTA Box */}
                <div className="my-10 p-6 border border-stone-200" style={{ backgroundColor: "oklch(0.97 0.005 75)" }}>
                  <p className="text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: "oklch(0.38 0.12 15)" }}>
                    Need help navigating Romanian auctions?
                  </p>
                  <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                    From NIF registration and SPV account setup to legal due diligence and power of attorney — our team has done this before. If you are a foreign investor interested in Romanian government or bank auctions, we can help you avoid the most common mistakes.
                  </p>
                  <a
                    href="/#contact"
                    className="inline-flex items-center gap-2 text-xs text-white px-5 py-2.5 transition-opacity hover:opacity-90 no-underline"
                    style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}
                  >
                    GET IN TOUCH <ChevronRight size={11} />
                  </a>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mt-8 mb-4">
                  {["Real estate", "Investment", "Romania", "ANAF", "Expat guide", "Property"].map((tag) => (
                    <span key={tag} className="text-xs px-3 py-1 border border-stone-200 text-stone-500 font-light">
                      {tag}
                    </span>
                  ))}
                </div>

                <FeaturedDeveloper />
              </article>
            </main>

            {/* Sidebar */}
            <aside className="w-full lg:w-72 flex-shrink-0">
              <div className="sticky top-24 flex flex-col gap-6">
                <SidebarAd />
                <div className="p-5 border border-stone-200 bg-white">
                  <h4 className="font-bold text-stone-900 mb-3" style={{ fontFamily: "Playfair Display, serif" }}>
                    About Brașov Gateway
                  </h4>
                  <p className="text-stone-500 text-sm font-light leading-relaxed mb-4">
                    We help foreigners move to, invest in, and build a life in Brașov. Real estate, company setup, relocation support — all in one place.
                  </p>
                  <a
                    href="/#contact"
                    className="block text-center text-xs text-white py-2.5 transition-opacity hover:opacity-90 no-underline"
                    style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}
                  >
                    GET IN TOUCH
                  </a>
                </div>
                <div className="mt-6 p-5" style={{ backgroundColor: "oklch(0.18 0.01 60)" }}>
                  <h4 className="font-bold text-white mb-2" style={{ fontFamily: "Playfair Display, serif" }}>
                    Stay in the loop
                  </h4>
                  <p className="text-stone-300 text-xs font-light leading-relaxed mb-4">
                    No spam. One email per week with what is actually happening in Brașov.
                  </p>
                  <NewsletterInlineLight />
                </div>
                <div className="p-5 text-white" style={{ backgroundColor: "oklch(0.38 0.12 15)" }}>
                  <p className="text-xs uppercase tracking-widest mb-3 opacity-70">Real estate services</p>
                  <h4 className="font-bold mb-3 leading-snug" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.1rem" }}>
                    Looking for an apartment in Brașov?
                  </h4>
                  <p className="text-sm font-light leading-relaxed mb-4 opacity-80">
                    We help foreigners find, evaluate, and buy property in Brașov — without the guesswork.
                  </p>
                  <Link href="/services/real-estate">
                    <button className="w-full text-xs py-2.5 border border-white/40 hover:bg-white/10 transition-colors tracking-widest">
                      VIEW SERVICE
                    </button>
                  </Link>
                </div>
              </div>
            </aside>
          </div>

          {/* Related articles */}
          <div className="mt-20 pt-12 border-t border-stone-200">
            <div className="flex items-center gap-3 mb-8">
              <div className="h-px w-8" style={{ backgroundColor: "oklch(0.38 0.12 15)" }} />
              <span className="text-xs uppercase tracking-widest text-stone-400">More from the blog</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {RELATED.map((post, i) => (
                <Link key={i} href={post.href} className="group cursor-pointer no-underline block">
                  <div className="overflow-hidden mb-4">
                    <img src={post.img} alt={post.title} className="w-full h-44 object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                  </div>
                  <span className="text-xs uppercase tracking-widest font-semibold" style={{ color: "oklch(0.38 0.12 15)" }}>
                    {post.tag}
                  </span>
                  <h3 className="mt-2 text-base font-semibold text-stone-900 group-hover:text-stone-600 transition-colors leading-snug" style={{ fontFamily: "Playfair Display, serif" }}>
                    {post.title}
                  </h3>
                  <div className="mt-3 flex items-center gap-1 text-xs font-semibold" style={{ color: "oklch(0.38 0.12 15)", letterSpacing: "0.08em" }}>
                    READ MORE <ChevronRight size={11} />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>

        <SiteFooter />
      </div>
    </>
  );
}
